import { Arrange, Spacer, Text, TextButton } from '@loomhq/lens';
import React from 'react';
import { useDispatch } from 'react-redux';

import { openModal } from 'actions/modals';
import {
  APPROACHING_CONTENT_LIMIT_CHIP_DISMISSED,
  HEADER_UPGRADE_TEXT_CLICKED,
  STORAGE_INCENTIVE_INVITE_TEAMMATES_CTA_CLICKED,
} from 'constants/events';
import { TEAM_INVITE_MODAL } from 'constants/modalTypes';
import {
  ORG_ROLE_ADMIN,
  ORG_ROLE_CREATOR_LITE,
} from 'constants/organizationRoles';
import { MANAGE_PLANS } from 'constants/routes';
import {
  WORKSPACE_PLAN_BUSINESS,
  WORKSPACE_PLAN_DISPLAY_NAME_MAP,
  WORKSPACE_PLAN_STARTER_FREE,
} from 'constants/workspacePlans';
import { UPGRADE_TYPES } from 'constants/workspaceUpgradeRequests';
import { requestUpgrade } from 'creators/upgrades';
import { useApproachingContentLimitChipState } from 'utilities/localStorage/storageIncentives';
import { getUpgradeType } from 'utilities/upgrades';

import {
  useFetchSelectedWorkspaceBillingDetails,
  useGetMemberVideoLimits,
  useGetSelectedWorkspace,
  useGetUserRoleForSelectedWorkspace,
  useGetWorkspaceMemberRoles,
} from 'hooks/workspace';

import * as analytics from '../../../utilities/analytics';
import * as billingUtil from '../../../utilities/billingAndPayments/billingDetailsUtil';
import EarnMoreVideosPrompt from '../../storage-incentives/EarnMoreVideosPrompt';

import HideDuringDowntime from 'components/hide-during-downtime';
import MediaQuery from 'components/media-query';
import { APPROACHING_CONTENT_LIMIT_CHIP } from 'components/storage-incentives/constants';

import styles from './styles.module.less';

const ProgressBar = ({ videosRecorded, videoLimit }) => {
  const percent = (videosRecorded / videoLimit) * 100;

  return (
    <meter min={0} max={100} value={percent} className={styles.progressBar} />
  );
};

export const UpgradePrompt = ({ videoPageView, minimal = false }) => {
  const dispatch = useDispatch();
  const workspace = useGetSelectedWorkspace();
  const billingDetails = useFetchSelectedWorkspaceBillingDetails();
  const memberRoles = useGetWorkspaceMemberRoles();
  const memberVideoLimit = useGetMemberVideoLimits();
  const userRole = useGetUserRoleForSelectedWorkspace();
  const isTrialing = billingUtil.isTrialing(billingDetails);
  const willBeActive = billingUtil.willBeActive(billingDetails);

  const [showApproachingLimitChip, setShowApproachingLimitChip] =
    useApproachingContentLimitChipState();

  const showUpgradePrompt =
    (!memberRoles.find(role => role === ORG_ROLE_CREATOR_LITE) &&
      workspace.type === WORKSPACE_PLAN_STARTER_FREE) ||
    (workspace.type === WORKSPACE_PLAN_BUSINESS && isTrialing && !willBeActive);

  if (!showUpgradePrompt) {
    return null;
  }

  const isSharePage = window.location.pathname.includes('/share/');

  const upgradeType = getUpgradeType({
    selectedWorkspace: workspace,
    minPlanForFeature: WORKSPACE_PLAN_BUSINESS,
  });

  const upgradeOnclick = () => {
    dispatch(
      requestUpgrade({
        analyticsEvent: HEADER_UPGRADE_TEXT_CLICKED,
        selectedWorkspace: workspace,
        minPlanForFeature: WORKSPACE_PLAN_BUSINESS,
      })
    );
  };

  const upgradeCopy =
    upgradeType === UPGRADE_TYPES.ROLE
      ? 'Request role upgrade'
      : userRole === ORG_ROLE_ADMIN
      ? 'Upgrade plan'
      : 'Request plan upgrade';

  if (memberVideoLimit.limit > 0) {
    if (minimal) {
      return (
        <>
          <HideDuringDowntime>
            <div className={styles.navbarUpgradePromptSpacing}>
              <Arrange gap="xsmall">
                <div className="flex flexDirection:column">
                  <Text color="bodyDimmed">
                    {memberVideoLimit.totalVideos}/{memberVideoLimit.limit}{' '}
                    videos
                  </Text>
                  <ProgressBar
                    videosRecorded={memberVideoLimit.totalVideos}
                    videoLimit={memberVideoLimit.limit}
                  />
                </div>
              </Arrange>
            </div>
          </HideDuringDowntime>
          {memberVideoLimit.remainingVideos <= 10 &&
            showApproachingLimitChip !== false && (
              <>
                <Spacer right="0.75rem" />
                <EarnMoreVideosPrompt
                  handleClick={() => {
                    dispatch(openModal(TEAM_INVITE_MODAL));

                    analytics.track(
                      STORAGE_INCENTIVE_INVITE_TEAMMATES_CTA_CLICKED,
                      {
                        source: APPROACHING_CONTENT_LIMIT_CHIP,
                      }
                    );
                  }}
                  handleClose={() => {
                    setShowApproachingLimitChip(false);

                    analytics.track(APPROACHING_CONTENT_LIMIT_CHIP_DISMISSED);
                  }}
                />
              </>
            )}
        </>
      );
    }

    return videoPageView ? (
      <HideDuringDowntime>
        <div className={styles.upgradePromptSpacing}>
          <Arrange gap="xsmall">
            <div className="flex flexDirection:column">
              <Text color="bodyDimmed">
                {memberVideoLimit.totalVideos}/{memberVideoLimit.limit} videos
              </Text>
              <ProgressBar
                videosRecorded={memberVideoLimit.totalVideos}
                videoLimit={memberVideoLimit.limit}
              />
            </div>
            <TextButton onClick={upgradeOnclick}>
              <Text color="primary">{upgradeCopy}</Text>
            </TextButton>
          </Arrange>
        </div>
      </HideDuringDowntime>
    ) : (
      <HideDuringDowntime>
        <Arrange gap="xsmall" columns="1fr" width="100%">
          <TextButton onClick={upgradeOnclick}>
            <Text color="primary" fontWeight="medium">
              {upgradeCopy}
            </Text>
          </TextButton>
          <Spacer left={1}>
            <Text color="bodyDimmed">
              {workspace.type === WORKSPACE_PLAN_BUSINESS &&
              userRole === ORG_ROLE_CREATOR_LITE
                ? 'Current role: Creator Lite'
                : `Current plan: ${
                    WORKSPACE_PLAN_DISPLAY_NAME_MAP[workspace.type]
                  }`}
            </Text>
            <Text color="bodyDimmed">
              {memberVideoLimit.totalVideos}/{memberVideoLimit.limit} videos
              saved in Workspace
            </Text>
          </Spacer>
        </Arrange>
      </HideDuringDowntime>
    );
  }

  if (showUpgradePrompt && memberVideoLimit.limit > 0) {
    return (
      <HideDuringDowntime>
        <MediaQuery query={`(min-width: ${isSharePage ? 1260 : 800}px)`}>
          <p>
            <a href={MANAGE_PLANS}>
              <Text color="blurple" isInline>
                Upgrade now
              </Text>
            </a>
          </p>
        </MediaQuery>
      </HideDuringDowntime>
    );
  }

  return null;
};

export default UpgradePrompt;
